import React from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(-50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  background: #1e293b;
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  .header-content {
    flex: 1;
  }

  h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.6rem;
    font-weight: 700;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.95rem;
  }

  .badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.15);
    padding: 0.35rem 0.7rem;
    border-radius: 6px;
    font-size: 0.7rem;
    margin-top: 0.5rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem 0.8rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 2rem;

  .detail-section {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e5e7eb;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    label {
      font-weight: 600;
      color: #000000;
      min-width: 150px;
    }

    value {
      color: #4b5563;
      text-align: right;
      flex: 1;
    }
  }

  .price-highlight {
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    border-left: 4px solid #1e293b;
    margin: 1.5rem 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .price-label {
      font-size: 0.9rem;
      color: #555;
      margin-bottom: 0.5rem;
    }

    .price {
      font-size: 2rem;
      color: #2e7d32;
      font-weight: 700;
    }
  }

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ActionButtons = styled.div`
  padding: 2rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 1rem;
  flex-direction: column;

  button {
    padding: 1rem;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    }

    &.contact {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;

      &:hover {
        box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
      }
    }

    &.order {
      background: #f0f0f0;
      color: #333;
      border: 2px solid #667eea;

      &:hover {
        background: #667eea;
        color: white;
      }
    }
  }
`;

const BuyerListingDetailsModal = ({ listing, onClose, onContact, onOrder }) => {
  if (!listing) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-content">
            <h2>🌾 {listing.cropType}</h2>
            <p>{listing.breedVariety || 'Standard Variety'}</p>
            <span className="badge">Verified Seller</span>
          </div>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {/* Price Highlight */}
          <div className="price-highlight">
            <div className="price-label">Price per kg</div>
            <div className="price">₹{listing.basePrice.toFixed(2)}</div>
          </div>

          {/* Crop Information */}
          <div className="detail-section">
            <div className="section-title">Crop Information</div>
            <div className="detail-row">
              <label>Crop Type:</label>
              <value>{listing.cropType}</value>
            </div>
            <div className="detail-row">
              <label>Variety:</label>
              <value>{listing.breedVariety || 'Not specified'}</value>
            </div>
            {listing.farmingDetails && (
              <div className="detail-row">
                <label>Farming Details:</label>
                <value>{listing.farmingDetails}</value>
              </div>
            )}
          </div>

          {/* Location Information */}
          <div className="detail-section">
            <div className="section-title">Location</div>
            <div className="detail-row">
              <label>State:</label>
              <value>{listing.state}</value>
            </div>
            <div className="detail-row">
              <label>Region:</label>
              <value>{listing.region}</value>
            </div>
            <div className="detail-row">
              <label>Cultivation Area:</label>
              <value>{listing.cultivationAreaInAcres} acres</value>
            </div>
          </div>

          {/* Availability & Orders */}
          <div className="detail-section">
            <div className="section-title">Availability & Orders</div>
            <div className="detail-row">
              <label>Available Quantity:</label>
              <value>{listing.harvestQuantityInKg.toLocaleString()} kg</value>
            </div>
            <div className="detail-row">
              <label>Minimum Order:</label>
              <value>{listing.minimumOrderQuantity.toLocaleString()} kg</value>
            </div>
          </div>

          {/* Seller Information */}
          <div className="detail-section">
            <div className="section-title">Seller Information</div>
            {listing.sellerName && (
              <div className="detail-row">
                <label>Seller Name:</label>
                <value>{listing.sellerName}</value>
              </div>
            )}
          </div>

          {/* Certification */}
          {listing.certificationUrl && (
            <div className="detail-section">
              <div className="section-title">Certification</div>
              <div className="detail-row">
                <label>Certificate:</label>
                <a href={listing.certificationUrl} target="_blank" rel="noopener noreferrer">
                  View Document ↗
                </a>
              </div>
            </div>
          )}
        </ModalBody>

        <ActionButtons>
          <button className="contact" onClick={() => { onContact && onContact(listing); onClose(); }}>
            💬 Contact Seller
          </button>
          <button className="order" onClick={() => { onOrder && onOrder(listing); onClose(); }}>
            🛒 Place Order
          </button>
        </ActionButtons>
      </ModalContainer>
    </Overlay>
  );
};

export default BuyerListingDetailsModal;
